import {
  getEligibleCategoriesFromSubRoles,
  CONTACT_CATEGORY,
  isActiveProviderUser,
} from "./requestContactDesignations";
import { coerceHyperComplyOrgId } from "./overviewContactsShared";
import { getUserType } from "./helperMethods";
import {
  hasMyVendorSponsorAdminRole,
  hasMyVendorVqaManagerRole,
} from "./myVendorsRequestAccess";
import {
  getUserSubRoleNames,
  normalizeOverviewContact,
  resolveSavedContactsFromDetails,
  resolveSponsorSubRoleSource,
} from "./overviewContactsShared";

export {
  extractEmailDomain,
  validateEmailDomainAgainstOrgUsers,
  formatPlatformRoleLabel,
  getUserSubRoleNames,
  contactSignature,
  normalizeOverviewContact,
  toRequestContactPayload,
  formatOverviewReadOnlyContact,
  pluralizeContactLabel,
} from "./overviewContactsShared";

/** Vendor Admin or VQA / Audit & Assessment Manager — not VQQ Managers. */
export function isVqaEligibleVendorUser(user) {
  if (!isActiveProviderUser(user)) return false;
  const categories = getEligibleCategoriesFromSubRoles(getUserSubRoleNames(user));
  return categories.includes(CONTACT_CATEGORY.ASSESSMENT);
}

function asRoleList(source) {
  return Array.isArray(source) ? source : [source];
}

function getSponsorAssessmentRoleNames(user) {
  return getUserSubRoleNames({
    sponsor_subrole_dto: asRoleList(resolveSponsorSubRoleSource(user)),
  });
}

function preferredSponsorAssessmentRole(roles) {
  return (
    roles.find((role) => hasMyVendorSponsorAdminRole([role])) ||
    roles.find((role) => hasMyVendorVqaManagerRole([role])) ||
    null
  );
}

function preferredVendorAssessmentRole(roles) {
  return roles.find(
    (role) => role === "Vendor Admin" || hasMyVendorVqaManagerRole([role]),
  );
}

function orderedRolePayload(roles, preferred) {
  return [preferred, ...roles.filter((role) => role !== preferred)].map(
    (sub_role) => ({ sub_role }),
  );
}

function contactForAssessmentPicker(user, mode) {
  if (mode === "sponsor") {
    const roles = getSponsorAssessmentRoleNames(user);
    const preferred = preferredSponsorAssessmentRole(roles);
    return {
      ...user,
      vendor_subrole_dto: undefined,
      provider_user_subrole: undefined,
      sponsor_subrole_dto: orderedRolePayload(roles, preferred),
      sponsor_sub_roles: undefined,
      sub_roles: undefined,
      subRoles: undefined,
      sub_role: undefined,
    };
  }

  const roles = getUserSubRoleNames(user);
  const preferred = preferredVendorAssessmentRole(roles);
  return {
    ...user,
    vendor_subrole_dto: orderedRolePayload(roles, preferred),
    provider_user_subrole: undefined,
  };
}

export function isVqqDisplayedRole(label) {
  const value = String(label || "").trim();
  return (
    value === "VQQ Manager" ||
    value.startsWith("VQQ Manager ") ||
    value.startsWith("VQQ Manager-")
  );
}

/** Sponsor Admin or Audit & Assessment Manager — not VQQ Managers. */
export function isVqaEligibleSponsorUser(user) {
  if (!isActiveProviderUser(user)) return false;
  return Boolean(preferredSponsorAssessmentRole(getSponsorAssessmentRoleNames(user)));
}

export function mapVqaOrgUsersToPickerContacts(users, { mode = "vendor" } = {}) {
  const eligibleFilter =
    mode === "sponsor" ? isVqaEligibleSponsorUser : isVqaEligibleVendorUser;

  return (users || [])
    .filter(eligibleFilter)
    .map((u, index) =>
      normalizeOverviewContact(contactForAssessmentPicker(u, mode), index),
    )
    .filter((contact) => contact && !isVqqDisplayedRole(contact.userRole))
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return String(a.name).localeCompare(String(b.name));
    });
}

export function resolveSavedVqaVendorContacts(details) {
  const fromRoot = resolveSavedContactsFromDetails(details, {
    listKeys: ["request_contacts", "requestContacts"],
    legacyEmailKeys: [],
  });
  if (fromRoot.length) return fromRoot;

  const provider = details?.provider;
  return resolveSavedContactsFromDetails(provider || details, {
    listKeys: [],
    legacyEmailKeys: ["provider_contact_email", "vendor_contact_email"],
  });
}

/**
 * Resolve sponsor contacts for a specific sponsor row (multi-sponsor) or top-level.
 */
export function resolveSavedVqaSponsorContacts(details, sponsorId = null) {
  const sponsors =
    details?.vqa_request_sponsors ||
    details?.vqa_sponsors ||
    details?.vqaSponsors ||
    [];

  if (Array.isArray(sponsors) && sponsors.length > 0) {
    const row = sponsorId
      ? sponsors.find((s) => String(s.sponsor_id) === String(sponsorId))
      : sponsors.find((s) => s.is_primary_sponsor) || sponsors[0];
    if (row) {
      const fromList = resolveSavedContactsFromDetails(row, {
        listKeys: ["sponsor_contacts", "sponsorContacts"],
        legacyEmailKeys: ["sponsor_contact_email"],
      });
      if (fromList.length) return fromList;
    }
  }

  return resolveSavedContactsFromDetails(details, {
    listKeys: ["sponsor_contacts", "sponsorContacts"],
    legacyEmailKeys: ["sponsor_contact_email"],
  });
}

const VQA_LOCKED_STATUSES = new Set([
  "complete",
  "final report",
  "final report delivered",
  "final report delivered to sponsor",
  "report anonymized",
  "report published to library",
  "archived",
  "cancelled",
]);

export function isVqaContactsLocked(status, isArchived = false) {
  if (isArchived) return true;
  const s = String(status || "").trim().toLowerCase();
  if (!s) return false;
  return VQA_LOCKED_STATUSES.has(s);
}

/**
 * Role matrix for Assessment Overview contact fields (story 14580).
 * @param {string} userRole from useUserRole()
 */
export function getVqaOverviewContactPermissions(userRole, status, isArchived = false) {
  const locked = isVqaContactsLocked(status, isArchived);
  const userType = getUserType(userRole);
  const isAdmin = userType === "admin";
  const isSponsor = userType === "sponsor";
  const isVendor = userType === "vendor";

  return {
    userType,
    locked,
    canEditVendor: !locked && (isAdmin || isSponsor || isVendor),
    canEditSponsor: !locked && (isAdmin || isSponsor),
    showVendorPlatformRoles: isAdmin || isVendor,
    showSponsorPlatformRoles: true,
    useAddContactLabelsOnVendor: isSponsor,
  };
}

export const VQA_MANAGER_CREATE_ROLES = {
  vendor: {
    provider_user_subrole: [{ Subrole: "VQA Manager" }],
    provider_user_permissions: [{ Permission: "VQA Requests" }],
  },
  sponsor: {
    sponsor_subrole_dto: [{ sub_role: "VQA Manager" }],
  },
};

/** Resolve assessment id for Overview contact APIs (vqa_id preferred, else request id). */
export function resolveVqaOverviewEntityId(details) {
  return (
    details?.vqa?.vqa_id ||
    details?.vqa_id ||
    details?.vqa?.[0]?.vqa_id ||
    details?.vqa_request_id ||
    null
  );
}

export function resolveVqaOverviewStatus(details) {
  if (
    String(details?.vqa_request_status || "").toLowerCase() === "archived" ||
    String(details?.vqa_status || "").toLowerCase() === "archived"
  ) {
    return { status: "Archived", isArchived: true };
  }
  const status =
    details?.vqa?.vqa_status ||
    details?.vqa?.[0]?.vqa_status ||
    details?.vqa_status ||
    details?.vqa_request_status ||
    "";
  return { status, isArchived: false };
}

export function resolveVqaProviderRecord(details) {
  return (
    details?.provider ||
    details?.vqa?.provider ||
    details?.vqa?.[0]?.provider ||
    null
  );
}

export function resolveVqaProviderId(details) {
  const provider = resolveVqaProviderRecord(details);
  return (
    provider?.provider_id ||
    details?.provider_id ||
    details?.providerId ||
    null
  );
}

export function resolveVqaProviderName(details) {
  const provider = resolveVqaProviderRecord(details);
  return (
    provider?.provider_name ||
    details?.provider_name ||
    details?.providerName ||
    ""
  );
}

/** HyperComply org id for vendor user provisioning (prefers live provider GET). */
export function resolveVqaVendorHyperComplyOrgId({
  providerOrgDetails,
  assessmentDetails,
} = {}) {
  const provider = resolveVqaProviderRecord(assessmentDetails);
  return coerceHyperComplyOrgId(
    providerOrgDetails?.hc_org_id,
    providerOrgDetails?.hcOrgId,
    assessmentDetails?.organizationId,
    assessmentDetails?.OrganizationId,
    provider?.hc_org_id,
    provider?.organization_id,
  );
}

export function resolveVqaSponsorId(details, preferredSponsorId = null) {
  if (preferredSponsorId) return preferredSponsorId;
  const sponsors =
    details?.vqa_request_sponsors || details?.vqa_sponsors || [];
  if (Array.isArray(sponsors) && sponsors.length > 0) {
    const primary = sponsors.find((s) => s.is_primary_sponsor) || sponsors[0];
    return primary?.sponsor_id || null;
  }
  return details?.sponsor_id || details?.sponsorId || null;
}
